/*
 * 中部组件：检查清单（main_step）
 * 每个维护步骤：开始当前步骤 -> 逐项检查（r1/r2/r3/n1/a1/i1/u1）-> 结束当前步骤（end_sub_check 阈值比较 + 异常确认）
 * 依赖：common/core.js ui.js biz.js
 */
export default function installMiddle(MW) {
  var h = MW.h, ui = MW.ui, biz = MW.biz, util = MW.util, store = MW.store, api = MW.api;
  var v = biz.v;

  var showErr = {}; // 步骤级校验错误展示（对应原 $form.fields["main_step.i.ob_main"].validate() 后的错误提示）
  var collapsed = {}; // 用户手动折叠/展开
  var fold = biz.folder(collapsed, function () {
    return biz.steps().map(function (m, i) { return { id: i, started: !!m.step_start_sw, ended: !!m.step_end_sw }; });
  });
  var busy = {}; // 步骤开关请求中
  var startLoading = false; // 开始维护请求中

  var OPTS = { r1: ["正常", "异常"], r2: ["正常", "异常", "不适用"], r3: ["已更换", "未更换"] };
  var NUM_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

  /* ---------- 条件（与原 schema x-reactions 一致） ---------- */
  function lock() { return biz.isClosed(); }
  function stepAt(i) { return biz.steps()[i] || {}; }
  /** 开始区：step_end_sw || !sw_start 时禁用 */
  function startDisabled(i) { var ms = stepAt(i); return lock() || !!ms.step_end_sw || !v("sw_start"); }
  /** 检查项：step_end_sw 时禁用 */
  function subDisabled(i) { return lock() || !!stepAt(i).step_end_sw; }
  /** 结束区：下一步已开始时禁用；最后一步在尾部【确认完成】已打开时禁用 */
  function endDisabled(i) { var ss = biz.steps(), n = ss[i + 1]; return lock() || (n ? n.step_start_sw === true : !!v("sw_end")); }

  function isAbn(s) { return (s.r1 || "") === "异常" || (s.r2 || "") === "异常"; }
  /** 子项当前可见（因此必填）的字段 */
  function subFields(s) {
    var f = [];
    var sr = String(s.s_r1);
    if (sr === "1") f.push("r1");
    if (sr === "2") f.push("r2");
    if (sr === "3") f.push("r3");
    if (String(s.s_n1) !== "0" && s.r2 !== "不适用") f.push("n1");
    if (isAbn(s)) f.push("a1", "i1");
    if (String(s.s_u1) !== "0" && s.r2 !== "不适用") f.push("u1");
    return f;
  }
  function key(i, j, f) { return "ms." + i + (j == null ? "" : "." + j) + "." + f; }

  /** 单个步骤的必填校验（隐藏字段不校验） */
  function stepErrors(i) {
    var ms = stepAt(i), e = [];
    if (!ms.step_start_sw) {
      e.push({ key: key(i, null, "start"), msg: "请打开开关" });
      return e; // 未开始时检查项与结束区均隐藏
    }
    (ms.sub_step || []).forEach(function (s, j) {
      subFields(s).forEach(function (f) {
        if (util.isEmpty(s[f])) e.push({ key: key(i, j, f), msg: "该字段是必填字段" });
        else if (f === "n1" && !NUM_RE.test(String(s.n1).trim())) e.push({ key: key(i, j, f), msg: "该字段不是有效的数字" });
      });
    });
    if (!ms.step_end_sw) e.push({ key: key(i, null, "end"), msg: "请打开开关" });
    return e;
  }

  /* ---------- 统计 ---------- */
  function stats() {
    var ms = biz.steps(), total = 0, filled = 0, done = 0, abn = 0;
    ms.forEach(function (m) {
      if (m.step_start_time && m.step_end_time) done++;
      (m.sub_step || []).forEach(function (s) {
        total++;
        var fs = subFields(s);
        if (m.step_start_sw && fs.every(function (f) { return !util.isEmpty(s[f]); })) filled++;
        if (isAbn(s)) abn++;
      });
    });
    return { steps: ms.length, done: done, total: total, filled: filled, abn: abn, pct: total ? Math.round((filled / total) * 100) : 0 };
  }
  function stepStatus(i) {
    var ms = stepAt(i);
    if (showErr[i] && stepErrors(i).length) return "error";
    if (ms.step_end_sw) return "done";
    if (ms.step_start_sw) return "doing";
    return "todo";
  }
  var ST = { todo: ["未开始", "muted", "ri-time-line"], doing: ["进行中", "info", "ri-loader-2-line"], done: ["已完成", "ok", "ri-checkbox-circle-line"], error: ["待完善", "danger", "ri-error-warning-line"] };

  /* ---------- 交互逻辑 ---------- */
  var sync = MW.sync;
  function stepPath(i, f) { return "main_step." + i + "." + f; }

  /** 开始维护（原头部 sw_start）：先拉一次最新数据再判断；已被他人打开时提示，不重复记录时间 */
  function onSwStart(val) {
    startLoading = true;
    store.touch("sw_start");
    sync.fresh()
      .then(function () {
        if (val && v("sw_start")) return sync.doneBy("sw_start", "开始维护", "打开");
        if (!biz.swStartEditable()) return ui.toast("数据已更新，请按最新状态操作", "warn");
        store.patch({ sw_start: val, txt_start: val ? util.now() : biz.TXT_START });
        return sync.commit(["sw_start", "txt_start"], val ? ["sw_start"] : []).then(function (r) {
          if (r && r.code === 1) return sync.doneBy("sw_start", "开始维护", "打开", r.rejected);
          if (val) {
            ui.toast(r && r.offline ? "已开始维护（当前无网络，已暂存在本机，联网后自动上传）" : "已开始维护，请按步骤逐项检查", r && r.offline ? "info" : "ok");
            collapsed[0] = false;
          }
        });
      })
      .catch(function (e) {
        if (util.isNetErr(e)) return ui.toast("网络不稳定，操作已暂存，联网后自动上传", "warn");
        ui.toast("保存失败：" + (util.errMsg(e) || "服务异常") + "，将自动重试", "danger");
      })
      .then(function () {
        startLoading = false;
        store.touch("sw_start");
      });
  }

  /** 开始当前步骤：先拉最新数据，再按服务器最新状态做顺序校验；已被他人开始时提示并刷新 */
  function onStartSw(i, val) {
    busy[i] = "start";
    store.touch("main_step");
    sync.fresh()
      .then(function () {
        var ms = stepAt(i);
        if (val && ms.step_start_sw) return sync.doneBy(stepPath(i, "step_start_sw"), "步骤 " + (ms.step_sn || i + 1), "开始");
        if (startDisabled(i)) return ui.toast("数据已更新，请按最新状态操作", "warn");
        // 校验上一步骤（main_step[i-1].ob_main）；第一步无上一步，直接通过
        var prevErr = i > 0 ? stepErrors(i - 1) : [];
        if (prevErr.length) {
          showErr[i - 1] = true;
          collapsed[i - 1] = false;
          ui.toast("请先完成步骤 " + (stepAt(i - 1).step_sn || i) + " 的必填项并结束该步骤", "warn");
          scrollToStep(i - 1, true);
          return;
        }
        ms.step_start_sw = val;
        ms.step_start_time = val ? util.now() : "";
        ms.step_start_user = val ? biz.user() : "";
        if (val) collapsed[i] = false;
        if (val && i > 0) collapsed[i - 1] = undefined;
        biz.saveLastPosition();
        store.touch("main_step");
        return sync.commit(
          [stepPath(i, "step_start_sw"), stepPath(i, "step_start_time"), stepPath(i, "step_start_user")],
          val ? [stepPath(i, "step_start_sw")] : []
        ).then(function (r) {
          if (r && r.code === 1) sync.doneBy(stepPath(i, "step_start_sw"), "步骤 " + (ms.step_sn || i + 1), "开始", r.rejected);
        });
      })
      .catch(function (e) {
        if (util.isNetErr(e)) return ui.toast("网络不稳定，操作已暂存，联网后自动上传", "warn");
        ui.toast("保存失败：" + (util.errMsg(e) || "服务异常") + "，将自动重试", "danger");
      })
      .then(function () {
        busy[i] = false;
        store.touch("main_step");
      });
  }

  /** 关闭结束开关 = 重新打开本步骤：仅在下一步骤未开始（最后一步：尾部未确认完成）时允许，清除结束信息后检查项恢复可编辑 */
  function reopenStep(i) {
    var ms = stepAt(i), sn = ms.step_sn || i + 1;
    ui.confirm("确定重新打开步骤 " + sn + "「" + (ms.step_name || "") + "」吗？结束时间将被清除，可重新修改本步骤的检查项。", { title: "重新打开步骤" }).then(function () {
      busy[i] = "end";
      store.touch("main_step");
      return sync.fresh()
        .then(function () {
          ms = stepAt(i);
          if (!ms.step_end_sw) return; // 已被他人重新打开，拉取后已是最新状态
          if (endDisabled(i)) return ui.toast("数据已更新，请按最新状态操作", "warn");
          ms.step_end_sw = false;
          ms.step_end_time = "";
          ms.step_end_user = "";
          collapsed[i] = false;
          biz.saveLastPosition();
          store.touch("main_step");
          // 服务器端兜底：下一步骤已开始 / 尾部已确认完成时整体拒绝（拒绝后自动拉取最新数据）
          var guard = biz.steps()[i + 1] ? [stepPath(i + 1, "step_start_sw")] : ["sw_end"];
          return sync.commit([stepPath(i, "step_end_sw"), stepPath(i, "step_end_time"), stepPath(i, "step_end_user")], guard).then(function (r) {
            if (r && r.code === 1) return ui.toast((guard[0] === "sw_end" ? "维护已确认完成" : "下一步骤已开始") + "，不能重新打开步骤 " + sn + "，已刷新为最新数据", "warn");
            if (r && r.offline) ui.toast("步骤 " + sn + " 已在本机重新打开（当前无网络），联网后自动上传", "info");
          });
        })
        .catch(function (e) {
          if (util.isNetErr(e)) return ui.toast("网络不稳定，操作已暂存，联网后自动上传", "warn");
          ui.toast("保存失败：" + (util.errMsg(e) || "服务异常") + "，将自动重试", "danger");
        })
        .then(function () {
          busy[i] = false;
          store.touch("main_step");
        });
    }, function () {});
  }

  /** 结束当前步骤：先拉最新数据并校验本步骤，再由服务器基于最新数据做阈值比较后结束 */
  function onEndSw(i, val) {
    var ms = stepAt(i);
    if (!val) return reopenStep(i);
    busy[i] = "end";
    store.touch("main_step");
    sync.fresh()
      .then(function () {
        ms = stepAt(i);
        var sn = ms.step_sn || i + 1;
        if (ms.step_end_sw) return sync.doneBy(stepPath(i, "step_end_sw"), "步骤 " + sn, "完成");
        if (endDisabled(i) || !ms.step_start_sw) return ui.toast("数据已更新，请按最新状态操作", "warn");
        ms.step_end_sw = true;
        var err = stepErrors(i);
        ms.step_end_sw = false;
        if (err.length) {
          showErr[i] = true;
          ui.toast("当前步骤还有 " + err.filter(function (e) { return !/\.end$/.test(e.key); }).length + " 项未填写", "warn");
          setTimeout(function () { ui.scrollToError(); }, 30);
          return;
        }
        showErr[i] = false;
        biz.saveLastPosition();
        if (!MW.net.offline) ui.loading(2000);
        return sync.endStep(i, util.now()).then(function (r) {
          if (r && r.code === 1) return sync.doneBy(stepPath(i, "step_end_sw"), "步骤 " + sn, "完成", r.rejected);
          collapsed[i] = undefined;
          if (r && r.offline) ui.toast("步骤 " + sn + " 已在本机结束（当前无网络），联网后自动上传，测点结果以服务器比较为准", "info");
          var cur = stepAt(i);
          var idx = (cur.sub_step || []).map(function (s, k) { return isAbn(s) ? sn + "." + (s.sub_step_sn || k + 1) : null; }).filter(Boolean);
          if (idx.length) {
            ui.confirm('<div class="color-danger">维护步骤' + idx.join("、") + "存在异常情况，请确认是否属实</div>", { title: "提示" }).catch(function () {});
          }
        });
      })
      .catch(function (e) {
        if (util.isNetErr(e)) return ui.toast("网络不稳定，结束步骤已暂存，联网后自动上传", "warn");
        ui.toast("结束步骤失败：" + (util.errMsg(e) || "服务异常") + "，请重试", "danger");
      })
      .then(function () {
        busy[i] = false;
        store.touch("main_step");
      });
  }

  /** 检查项：选择类立即保存这一项；输入类键入时 1 秒防抖保存，失焦立即保存 */
  function setSub(i, j, f, val, opt) {
    var s = (stepAt(i).sub_step || [])[j];
    if (!s) return;
    s[f] = val;
    var path = "main_step." + i + ".sub_step." + j + "." + f;
    if (opt && opt.silent) {
      sync.typing(path);
      return;
    }
    sync.save(path);
    store.touch("main_step");
  }

  function scrollToStep(i, center) {
    setTimeout(function () {
      var el = MW.$("#mw-step-" + i);
      if (!el) return;
      var top = util.pageTop(el) - (util.isMobileView() ? 12 : 16);
      util.scrollTo(center ? top - 40 : top, true);
    }, 40);
  }

  /* ---------- 渲染：概览 ---------- */
  function ring(pct) {
    var r = 26, c = 2 * Math.PI * r;
    var svg = '<svg viewBox="0 0 64 64"><circle cx="32" cy="32" r="' + r + '" class="bg"/><circle cx="32" cy="32" r="' + r + '" class="fg" stroke-dasharray="' + c + '" stroke-dashoffset="' + (c * (1 - pct / 100)) + '"/></svg>';
    return h("div", { class: "mw-ring", html: svg + "<b>" + pct + "<small>%</small></b>" });
  }

  /** 开始维护开关：检查清单的第一步 */
  function startRow(ctx) {
    var editable = biz.swStartEditable();
    var on = !!v("sw_start");
    var tip = null;
    if (!editable && !lock()) {
      var ms0 = biz.steps()[0] || {};
      if (ms0.step_start_sw) tip = "检查清单已开始，不可关闭";
      else if (!on) tip = "请先在上方完成打卡、" + (biz.isSelf() ? "人脸识别、设备核对" : "上传设备照片") + "，再开始维护";
    }
    return h(
      "div",
      { class: "mw-start", id: "mw-start" },
      biz.switchRow({
        title: "开始维护", required: true, key: "sw_start", icon: "ri-play-circle-line", time: v("txt_start") || biz.TXT_START,
        value: on, disabled: !editable, loading: startLoading, onChange: onSwStart, error: ctx.err("sw_start"), tip: tip, cls: "mw-swrow--hero",
      })
    );
  }

  function overview(ctx) {
    var s = stats();
    var ms = biz.steps();
    var curIdx = -1;
    for (var k = 0; k < ms.length; k++) { if (!ms[k].step_end_sw) { curIdx = k; break; } }
    var cur = !v("sw_start") ? "未开始" : curIdx < 0 ? (ms.length ? "全部完成" : "—") : "步骤 " + (ms[curIdx].step_sn || curIdx + 1);
    var stat = function (icon, label, val, tone) {
      return h("div", { class: ["mw-stat", tone && "is-" + tone] }, h("i", { class: icon }), h("div", null, h("small", null, label), h("b", null, val)));
    };
    return ui.panel(
      { title: "检查清单", icon: "ri-list-check-3", id: "mw-checklist", sub: "开始维护 → 按步骤顺序：开始步骤 → 逐项检查 → 结束步骤", cls: "mw-overview" },
      startRow(ctx),
      h(
        "div",
        { class: "mw-overview__top" },
        ring(s.pct),
        h(
          "div",
          { class: "mw-overview__stats" },
          stat("ri-stack-line", "维护步骤", s.done + " / " + s.steps),
          stat("ri-checkbox-multiple-line", "检查项已填", s.filled + " / " + s.total, "info"),
          stat("ri-alarm-warning-line", "异常项", s.abn, s.abn ? "danger" : "ok"),
          stat("ri-focus-3-line", "当前进度", cur, !v("sw_start") ? "muted" : curIdx < 0 ? "ok" : "info")
        )
      ),
      h(
        "div",
        { class: "mw-stepnav", role: "tablist" },
        ms.map(function (m, i) {
          var st = stepStatus(i);
          return h(
            "button",
            { type: "button", class: ["mw-stepnav__item", "is-" + st], onClick: function () { collapsed[i] = false; store.touch("main_step"); scrollToStep(i); } },
            h("span", { class: "mw-stepnav__no" }, st === "done" ? h("i", { class: "ri-check-line" }) : st === "error" ? h("i", { class: "ri-error-warning-line" }) : m.step_sn || i + 1),
            h("span", { class: "mw-stepnav__name" }, m.step_name)
          );
        })
      )
    );
  }

  /* ---------- 渲染：检查项 ---------- */
  function subCard(i, j, s, ctx) {
    var dis = subDisabled(i);
    var show = showErr[i] || ctx.submitted;
    var err = function (f) { return show && ctx.err(key(i, j, f)) || (show && localErr(i, j, f)); };
    var fs = subFields(s);
    var abn = isAbn(s);
    var sn = (stepAt(i).step_sn || i + 1) + "." + (s.sub_step_sn || j + 1);
    var filled = fs.every(function (f) { return !util.isEmpty(s[f]); });
    var pts = s.sub_measure_point_list || [];
    var hasErr = fs.some(function (f) { return err(f); });

    var ctrls = [];
    ["r1", "r2", "r3"].forEach(function (f) {
      if (fs.indexOf(f) < 0) return;
      ctrls.push(ui.field({ label: "检查结果", required: true, key: key(i, j, f), error: err(f), cls: "mw-sub__result" },
        ui.radio({ options: OPTS[f], value: s[f], disabled: dis, block: true, onChange: function (val) { setSub(i, j, f, val); } })));
    });
    if (fs.indexOf("n1") > -1) {
      ctrls.push(ui.field({
        label: "数值", required: true, key: key(i, j, "n1"), error: err("n1"),
        desc: pts.length ? pts.map(function (p) { return p.measure_point_name + (p.min_value != null && p.max_value != null ? "：阈值 " + p.min_value + " ~ " + p.max_value : "：无阈值"); }).join("；") : null,
      }, ui.input({ name: "ms_" + i + "_" + j + "_n1", value: s.n1, inputmode: "decimal", placeholder: "请输入数值", disabled: dis,
        onInput: function (val) { setSub(i, j, "n1", val, { silent: true }); },
        onBlur: function () { setSub(i, j, "n1", s.n1); } })));
    }
    if (!util.isEmpty(s.z2)) {
      var z = [].concat(s.z2).join("；");
      ctrls.push(h("div", { class: ["mw-alert", /超出/.test(z) ? "mw-alert--danger" : "mw-alert--info", "mw-sub__z2"] }, h("i", { class: "ri-dashboard-3-line" }), h("div", null, h("b", null, "测点结果："), z)));
    }
    if (abn) {
      ctrls.push(ui.grid(2, [
        ui.field({ label: "异常部位", required: true, key: key(i, j, "a1"), error: err("a1") },
          ui.tagSelect({ value: s.a1, options: v("abnormal_area") || [], disabled: dis, title: "异常部位", placeholder: "选择或输入异常部位",
            onChange: function (val) { setSub(i, j, "a1", val, { silent: true }); }, onBlur: function () { setSub(i, j, "a1", s.a1); } })),
        ui.field({ label: "异常描述", required: true, key: key(i, j, "i1"), error: err("i1") },
          ui.tagSelect({ value: s.i1, options: v("abnormal_description") || [], disabled: dis, title: "异常描述", placeholder: "选择或输入异常描述",
            onChange: function (val) { setSub(i, j, "i1", val, { silent: true }); }, onBlur: function () { setSub(i, j, "i1", s.i1); } })),
      ], "mw-grid--m1 mw-sub__abn"));
    }
    if (fs.indexOf("u1") > -1) {
      ctrls.push(ui.field({ label: "上传", required: true, key: key(i, j, "u1"), error: err("u1") },
        ui.upload({ value: s.u1, maxCount: 10, text: "上传文件/照片", disabled: dis, onChange: function (list) { setSub(i, j, "u1", list); } })));
    }

    return h(
      "article",
      { class: ["mw-sub", abn && "is-abn", filled && !abn && "is-ok", hasErr && "has-error"], style: { animationDelay: Math.min(j, 8) * 30 + "ms" } },
      h(
        "div",
        { class: "mw-sub__info" },
        h("div", { class: "mw-sub__head" },
          h("span", { class: "mw-sub__sn" }, sn),
          h("h4", null, String(s.t1 || "").trim()),
          abn ? ui.tag("异常", "danger", "ri-error-warning-fill") : filled ? ui.tag("已填", "ok", "ri-check-line") : null),
        s.t2 && h("p", { class: "mw-sub__desc" }, String(s.t2).trim()),
        pts.length > 0 && h("div", { class: "mw-sub__pts" }, pts.map(function (p) { return h("span", null, h("i", { class: "ri-sensor-line" }), p.measure_point_name); }))
      ),
      h("div", { class: "mw-sub__ctrl" }, ctrls)
    );
  }
  /** 字段级错误（步骤开关触发校验时使用；提交校验走 ctx.err） */
  function localErr(i, j, f) {
    var k = key(i, j, f);
    var e = stepErrors(i).filter(function (x) { return x.key === k; })[0];
    return e && e.msg;
  }

  /* ---------- 渲染：步骤 ---------- */
  function stepUsers(ms) {
    var a = ms.step_start_user || "", b = ms.step_end_user || "";
    if (a && b && a !== b) return a + " → " + b;
    return a || b || "";
  }
  function stepCard(i, ctx) {
    var ms = stepAt(i);
    var st = stepStatus(i);
    var subs = ms.sub_step || [];
    var started = !!ms.step_start_sw;
    var isCollapsed = fold.is(i, ms.step_end_sw);
    var show = showErr[i] || ctx.submitted;
    var errOf = function (f) { if (!show) return null; var k = key(i, null, f); var e = stepErrors(i).filter(function (x) { return x.key === k; })[0]; return e && e.msg; };
    var abnN = subs.filter(isAbn).length;
    var filledN = subs.filter(function (s) { return subFields(s).every(function (f) { return !util.isEmpty(s[f]); }); }).length;
    var sDis = startDisabled(i);
    var eDis = endDisabled(i);
    var next = biz.steps()[i + 1];

    return h(
      "section",
      { class: ["mw-step", "is-" + st, isCollapsed && "is-collapsed", st === "error" && "has-error"], id: "mw-step-" + i, style: { animationDelay: Math.min(i, 6) * 40 + "ms" } },
      h(
        "header",
        { class: "mw-step__head", onClick: function () { if (!started) return; collapsed[i] = !isCollapsed; store.touch("main_step"); } },
        h("span", { class: "mw-step__no" }, st === "done" ? h("i", { class: "ri-check-line" }) : ms.step_sn || i + 1),
        h("div", { class: "mw-step__title" },
          h("small", null, "维护步骤 " + (ms.step_sn || i + 1)),
          h("h3", null, ms.step_name),
          h("div", { class: "mw-step__meta" },
            ui.tag(ST[st][0], ST[st][1], ST[st][2]),
            started && h("span", null, h("i", { class: "ri-checkbox-multiple-line" }), filledN + "/" + subs.length + " 项"),
            abnN > 0 && h("span", { class: "is-danger" }, h("i", { class: "ri-alarm-warning-line" }), "异常 " + abnN),
            ms.step_start_time && h("span", null, h("i", { class: "ri-play-line" }), String(ms.step_start_time).slice(11, 16)),
            ms.step_end_time && h("span", null, h("i", { class: "ri-stop-line" }), String(ms.step_end_time).slice(11, 16)),
            stepUsers(ms) && h("span", { class: "mw-step__user", title: "步骤操作人" }, h("i", { class: "ri-user-3-line" }), stepUsers(ms)))),
        started && h("button", { type: "button", class: "mw-step__toggle", "aria-label": isCollapsed ? "展开" : "收起" }, h("i", { class: "ri-arrow-down-s-line" }))
      ),
      h(
        "div",
        { class: "mw-step__body" },
        biz.switchRow({
          title: "开始当前步骤", required: true, key: key(i, null, "start"), icon: "ri-play-circle-line",
          time: ms.step_start_time ? "步骤开始时间：" + ms.step_start_time : "打开开关记录步骤开始时间",
          value: !!ms.step_start_sw, disabled: sDis, loading: busy[i] === "start", error: errOf("start"),
          tip: sDis && !lock() ? (!v("sw_start") ? "请先开始维护" : ms.step_end_sw ? "步骤已结束" : null) : null,
          onChange: function (val) { onStartSw(i, val); }, cls: "mw-step__sw",
        }),
        started && !isCollapsed && h("div", { class: "mw-step__subs" }, subs.map(function (s, j) { return subCard(i, j, s, ctx); })),
        started && isCollapsed && h("button", { type: "button", class: "mw-step__fold", onClick: function () { collapsed[i] = false; store.touch("main_step"); } },
          h("i", { class: "ri-eye-line" }), "查看 " + subs.length + " 个检查项"),
        started &&
          biz.switchRow({
            title: "结束当前步骤", required: true, key: key(i, null, "end"), icon: "ri-flag-2-line",
            time: ms.step_end_time ? "步骤结束时间：" + ms.step_end_time : "完成全部检查项后打开开关",
            value: !!ms.step_end_sw, disabled: eDis, loading: busy[i] === "end", error: errOf("end"),
            tip: eDis && !lock() ? (next ? "下一步骤已开始，不可修改" : "维护已确认完成，不可修改") : null,
            onChange: function (val) { onEndSw(i, val); }, cls: "mw-step__sw mw-step__sw--end",
          })
      )
    );
  }

  biz.part({
    name: "middle",
    title: "检查清单",
    fold: fold,
    render: function (ctx) {
      var ms = biz.steps();
      return h(
        "div",
        { class: ["mw-middle", !v("sw_start") && "is-locked"] },
        overview(ctx),
        ms.length
          ? h("div", { class: "mw-steplist" }, ms.map(function (m, i) { return stepCard(i, ctx); }))
          : h("div", { class: "mw-panel" }, h("div", { class: "mw-empty" }, h("i", { class: "ri-inbox-2-line" }), "暂无检查清单"))
      );
    },
    errors: function () {
      var e = [];
      biz.req(e, "sw_start", v("sw_start"), "请打开开关");
      return biz.steps().reduce(function (acc, m, i) { return acc.concat(stepErrors(i)); }, e);
    },
    /** 多人同步：只替换有变化的步骤卡片与顶部概览，其余步骤不动 */
    partial: function (ctx, info, el) {
      var list = el.querySelector(".mw-steplist");
      if (!list || list.children.length !== biz.steps().length) return false;
      var y = util.scrollY();
      var snap = MW.anchor.capture();
      info.steps.forEach(function (i) {
        var old = el.querySelector("#mw-step-" + i);
        if (!old) return;
        var node = stepCard(i, ctx);
        node.style.animationDelay = "0ms";
        node.classList.add("is-static");
        if (info.flash && info.flash.indexOf(i) > -1) node.classList.add("mw-flash");
        old.parentNode.replaceChild(node, old);
      });
      var ov = el.querySelector("#mw-checklist");
      if (ov) ov.parentNode.replaceChild(overview(ctx), ov);
      if (!MW.anchor.restore(snap) && Math.abs(util.scrollY() - y) > 2) util.scrollTo(y);
      return true;
    },
    /** "回到上次填写的位置"：检查项所在步骤已折叠时先展开 */
    reveal: function (k) {
      var m = /^ms\.(\d+)\.\d+\./.exec(k || "");
      if (!m) return false;
      var i = Number(m[1]);
      if (!stepAt(i).step_start_sw) return false;
      collapsed[i] = false;
      store.touch("main_step");
      return true;
    },
  });
}
